import React, { MutableRefObject, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Avatar, Typography, Theme, createStyles } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DataGrid } from 'components';
import { DataGridProps, SearchDataProps } from 'components/DataGrid/types';
import { ColumnConfig, DataConnection } from 'hooks';
import { getInitials, toDateTimeString } from 'helpers';
import { PeriodsGridItemFragment, useRegularNeedsPeriodsQuery } from 'schema/serverTypes';
import { PeriodsSearch } from './PeriodsSearch';
import { StatusIcon } from './StatusIcon';
import { usePeriodsState } from './usePeriodsState';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    nameContainer: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
      color: 'inherit',
    },
    avatar: {
      marginRight: theme.spacing(2),
    },
  })
);

export const usePeriodsColumns = (): ColumnConfig<PeriodsGridItemFragment>[] => {
  const classes = useStyles();

  return useMemo<ColumnConfig<PeriodsGridItemFragment>[]>(
    () => [
      {
        id: 'ward',
        header: 'Подопечный',
        style: { width: '35%' },
        render: (period) => {
          const { ward } = period;
          const { name, photo } = ward.publicInformation;
          return (
            <Link to={`/wards/manage/${ward.id}`} className={classes.nameContainer}>
              <Avatar className={classes.avatar} src={photo.avatar}>
                {getInitials(name.fullName)}
              </Avatar>
              <Typography variant="body1">{name.fullName}</Typography>
            </Link>
          );
        },
      },
      {
        id: 'city',
        header: 'Город',
        style: { width: '20%' },
        render: (period) => period.ward.publicInformation.city,
      },
      {
        id: 'hasPhoto',
        header: 'Фото',
        style: { width: '10%' },
        align: 'center',
        render: (period) => {
          const to = `/wards/manage/${period.ward.id}/needs/${period.need.id}/period/${period.id}`;
          return <StatusIcon ok={period.hasPhoto} to={to} ariaLabel="Фото" />;
        },
      },
      {
        id: 'hasDocument',
        header: 'Документ',
        style: { width: '10%' },
        align: 'center',
        render: (period) => {
          const to = `/wards/manage/${period.ward.id}/needs/${period.need.id}/period/${period.id}`;
          return <StatusIcon ok={period.hasDocument} to={to} ariaLabel="Документ" />;
        },
      },
      {
        id: 'reportSentAt',
        header: 'Дата отправки',
        style: { width: '25%' },
        align: 'center',
        render: (period) =>
          period.reportSentAt ? toDateTimeString(new Date(period.reportSentAt as string)) : '—',
      },
    ],
    [classes]
  );
};

interface PeriodsGridProps {
  state: ReturnType<typeof usePeriodsState>;
  refetchRef: MutableRefObject<(() => void) | undefined>;
}

const getDataKey = (period: PeriodsGridItemFragment) => period.id;

export const PeriodsGrid = ({ state, refetchRef }: PeriodsGridProps) => {
  const {
    variables,
    onSearch,
    onSort,
    onPage,
    extractPagedData,
    columns,
    attachments,
    onAttachmentsChange,
  } = state;

  const SearchComponent = useMemo(
    () => (props: SearchDataProps<string>) => (
      <PeriodsSearch
        onSearch={props.onSearch}
        attachments={attachments}
        onAttachmentsChange={onAttachmentsChange}
      />
    ),
    [attachments, onAttachmentsChange]
  );

  const { data, loading, error, refetch } = useRegularNeedsPeriodsQuery({
    variables,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch, refetchRef]);

  useEffect(() => {
    if (error) {
      toast.error('Ошибка загрузки периодов');
    }
  }, [error]);

  if (error) {
    return null;
  }

  const entities = data?.regularNeedsPeriods as DataConnection<PeriodsGridItemFragment>;
  const { data: periods, count, startCursor, endCursor, page, rowsPerPage } = extractPagedData(
    entities
  );

  const gridProps: DataGridProps<PeriodsGridItemFragment, string> = {
    data: periods,
    onSort,
    onPage,
    onSearch,
    columns,
    getDataKey,
    count,
    startCursor,
    endCursor,
    page,
    searchComponent: SearchComponent,
    rowsPerPage,
    loading,
  };

  return <DataGrid<PeriodsGridItemFragment, string> {...gridProps} />;
};
